import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, UserPlus, Copy, KeyRound, Trash2, Pencil, Check } from "lucide-react";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PromptModal, ConfirmModal } from "@/components/AppDialogs";
import { toast } from "sonner";

const ROLES = [
  { v: "super_admin", label: "Super Admin", color: "#D81B8C" },
  { v: "commercial",  label: "Commercial",  color: "#FF8A00" },
  { v: "operations",  label: "Opérations",  color: "#6B2C8F" },
  { v: "logistics",   label: "Logistique",  color: "#206F18" },
  { v: "finance",     label: "Finances",    color: "#3FA535" },
  { v: "hr",          label: "RH",          color: "#206f8b" },
];

const roleMeta = (r) => ROLES.find((x) => x.v === r) || { label: r, color: "#5C4664" };

export default function AdminHR() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isSuperAdmin = user?.role === "super_admin" || user?.role === "admin";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "commercial" });
  const [creating, setCreating] = useState(false);
  const [tempPwd, setTempPwd] = useState(null);
  const [editRoleId, setEditRoleId] = useState(null);
  const [editRoleValue, setEditRoleValue] = useState("");
  const [error, setError] = useState("");
  const [resetPwdFor, setResetPwdFor] = useState(null); // user
  const [deleteUserFor, setDeleteUserFor] = useState(null); // user

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const submitCreate = async (e) => {
    e.preventDefault();
    setError("");
    setCreating(true);
    setTempPwd(null);
    try {
      const { data } = await api.post("/admin/users", form);
      setTempPwd(data.temporary_password || null);
      setForm({ email: "", name: "", role: "commercial" });
      await refresh();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setCreating(false);
    }
  };

  const resetPassword = (u) => setResetPwdFor(u);
  const submitResetPassword = async (pwd) => {
    const u = resetPwdFor;
    setResetPwdFor(null);
    if (!u) return;
    try {
      const body = pwd ? { password: pwd } : { password: Math.random().toString(36).slice(2, 12) };
      const { data } = await api.patch(`/admin/users/${u.id}`, body);
      toast.success(
        `Mot de passe mis à jour pour ${data.email}.${!pwd ? ` Provisoire : ${body.password}` : ""}`,
        { duration: 8000 },
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  };

  const saveRole = async (u) => {
    if (!editRoleValue || editRoleValue === u.role) { setEditRoleId(null); return; }
    try {
      await api.patch(`/admin/users/${u.id}`, { role: editRoleValue });
      setEditRoleId(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  };

  const remove = (u) => setDeleteUserFor(u);
  const performRemove = async () => {
    const u = deleteUserFor;
    setDeleteUserFor(null);
    if (!u) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      refresh();
      toast.success(`${u.email} supprimé.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF9FB] pb-24" data-testid="admin-hr-page">
      <TopBar />
      <div className="max-w-5xl mx-auto px-5 pt-6">
        {isSuperAdmin && (
          <button
            onClick={() => navigate("/admin")}
            data-testid="hr-back"
            className="inline-flex items-center gap-2 text-sm text-[#5C4664] hover:text-[#2A0E3C] mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> Tableau de bord
          </button>
        )}

        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#206f8b] mb-2">
              Équipe Milkado
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C]">
              Ressources Humaines
            </h1>
            <p className="mt-2 text-sm text-[#5C4664]">
              Connecté en tant que <strong>{user?.email}</strong> — rôle <strong>{user?.role}</strong>
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => { setCreateOpen((o) => !o); setTempPwd(null); setError(""); }}
              data-testid="hr-toggle-create"
              className="rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white gap-2"
            >
              <UserPlus className="w-4 h-4" /> Nouvel utilisateur
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="hr-logout"
              className="rounded-full border-[#F2E2EC] gap-2"
            >
              <LogOut className="w-4 h-4" /> Déconnexion
            </Button>
          </div>
        </div>

        {createOpen && (
          <form
            onSubmit={submitCreate}
            data-testid="hr-create-form"
            className="mt-6 bg-white rounded-2xl border border-[#F2E2EC] p-5 grid grid-cols-1 md:grid-cols-4 gap-3"
          >
            <div className="md:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">Email</Label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="hr-input-email"
                className="mt-1 h-11 rounded-xl border-[#F2E2EC]"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">Nom complet</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="hr-input-name"
                className="mt-1 h-11 rounded-xl border-[#F2E2EC]"
              />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">Rôle</Label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="hr-input-role"
                className="mt-1 w-full h-11 rounded-xl border border-[#F2E2EC] bg-white px-3 text-sm focus:ring-2 focus:ring-[#D81B8C] focus:outline-none"
              >
                {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-1 flex items-end">
              <Button
                type="submit"
                disabled={creating}
                data-testid="hr-submit-create"
                className="w-full h-11 rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white font-semibold"
              >
                {creating ? "…" : "Créer"}
              </Button>
            </div>
            {error && (
              <p data-testid="hr-create-error" className="md:col-span-4 text-sm text-[#D81B8C] font-medium">
                {error}
              </p>
            )}
            {tempPwd && (
              <div
                data-testid="hr-temp-pwd"
                className="md:col-span-4 flex items-center justify-between gap-3 bg-[#FFF5FA] border border-[#FFB3D9] rounded-xl px-4 py-3"
              >
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#D81B8C]">
                    Mot de passe provisoire — à transmettre une fois
                  </p>
                  <p className="font-mono text-sm text-[#2A0E3C] mt-0.5">{tempPwd}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { navigator.clipboard.writeText(tempPwd); }}
                  className="inline-flex items-center gap-1 text-[#D81B8C] text-sm font-semibold"
                  data-testid="hr-copy-temp-pwd"
                >
                  <Copy className="w-3.5 h-3.5" /> Copier
                </button>
              </div>
            )}
          </form>
        )}

        <div className="mt-8 bg-white rounded-2xl border border-[#F2E2EC] overflow-hidden" data-testid="hr-users-list">
          {loading ? (
            <p className="p-6 text-sm text-[#5C4664]">Chargement…</p>
          ) : users.length === 0 ? (
            <p className="p-6 text-sm text-[#5C4664]">Aucun utilisateur.</p>
          ) : (
            <ul className="divide-y divide-[#F2E2EC]">
              {users.map((u) => {
                const meta = roleMeta(u.role);
                const isSelf = u.id === user?.id;
                return (
                  <li key={u.id} data-testid={`hr-user-${u.id}`} className="px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <p className="font-display font-black text-[#2A0E3C] truncate">{u.name || "—"}</p>
                      <p className="text-xs text-[#5C4664] truncate">{u.email}</p>
                    </div>
                    {editRoleId === u.id ? (
                      <div className="flex items-center gap-2">
                        <select
                          value={editRoleValue}
                          onChange={(e) => setEditRoleValue(e.target.value)}
                          data-testid={`hr-edit-role-select-${u.id}`}
                          className="h-9 rounded-full border border-[#F2E2EC] bg-white px-3 text-sm"
                        >
                          {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                        </select>
                        <button
                          onClick={() => saveRole(u)}
                          data-testid={`hr-edit-role-save-${u.id}`}
                          className="w-9 h-9 rounded-full bg-[#3FA535] text-white flex items-center justify-center"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => { setEditRoleId(u.id); setEditRoleValue(u.role); }}
                        data-testid={`hr-role-${u.id}`}
                        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-bold uppercase tracking-widest"
                        style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
                      >
                        {meta.label}
                        <Pencil className="w-3 h-3 opacity-60" />
                      </button>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => resetPassword(u)}
                        data-testid={`hr-reset-${u.id}`}
                        title="Réinitialiser le mot de passe"
                        className="w-9 h-9 rounded-full border border-[#F2E2EC] hover:border-[#D81B8C] text-[#5C4664] hover:text-[#D81B8C] flex items-center justify-center"
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(u)}
                        disabled={isSelf}
                        data-testid={`hr-delete-${u.id}`}
                        title={isSelf ? "Vous ne pouvez pas supprimer votre propre compte." : "Supprimer l'utilisateur"}
                        aria-hidden={isSelf}
                        className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${
                          isSelf
                            ? "invisible"
                            : "border-[#F2E2EC] hover:border-red-500 text-[#5C4664] hover:text-red-500"
                        }`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <PromptModal
        open={!!resetPwdFor}
        onClose={() => setResetPwdFor(null)}
        onSubmit={submitResetPassword}
        title={resetPwdFor ? `Réinitialiser le mot de passe de ${resetPwdFor.email}` : ""}
        description="Laissez vide pour générer un mot de passe provisoire aléatoire."
        placeholder="Nouveau mot de passe (facultatif)"
        required={false}
        submitLabel="Réinitialiser"
        testId="hr-reset-pwd-modal"
      />

      <ConfirmModal
        open={!!deleteUserFor}
        onClose={() => setDeleteUserFor(null)}
        onConfirm={performRemove}
        title="Supprimer cet utilisateur ?"
        description={deleteUserFor ? `${deleteUserFor.email} sera définitivement supprimé. Cette action est irréversible.` : ""}
        confirmLabel="Supprimer"
        destructive
        testId="hr-delete-user-confirm"
      />
    </div>
  );
}
